import {
  File,
  StoragePathType,
  useGenerateUploadUrlMutation,
  useMakeFilePublicMutation,
} from "../graphql/generated/graphql";
import * as DocumentPicker from "expo-document-picker";
import axios from "axios";
import { useAppConfig } from "../AppConfigProvider";

export const generatePublicUrl = (currentDomain: string, filePath: string) => {
  let publicUrl = "";
  if (currentDomain === "axiodev.xyz") {
    publicUrl = `http://localhost:4443/storage/v1/b/primerai-dev-storage/o/${filePath}?alt=media`;
  } else {
    publicUrl = `https://${currentDomain}/${filePath}`;
  }

  return publicUrl;
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export type ACCEPTED_MIME_TYPE =
  | "application/pdf" // PDF
  | "application/vnd.openxmlformats-officedocument.wordprocessingml.document" // DOCX
  | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" // XLSX
  | "application/vnd.openxmlformats-officedocument.presentationml.presentation" // PPTX
  | "application/vnd.ms-excel" // XLS
  | "application/msword" // DOC
  | "text/csv" // CSV
  | "text/plain" // TXT
  | "image/png" // PNG
  | "image/jpeg" // JPEG/JPG
  | "image/jpg" // JPG alternative MIME type
  | "image/gif" // GIF
  | "image/webp" // WEBP
  | "image/x-icon" // ICO
  | "image/vnd.microsoft.icon" // ICO
  | "image/svg+xml" // SVG
  | "application/octet-stream"; // OCTET_STREAM

export const ACCEPTED_MIME_TYPES_ARRAY: ACCEPTED_MIME_TYPE[] = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/msword",
  "text/csv",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/x-icon",
  "image/vnd.microsoft.icon",
  "image/svg+xml",
  "application/octet-stream",
];

export interface ProcessedFile {
  file: File;
  signedUrl: string;
  mimeType: string;
  size: number;
  localUploadedFile: DocumentPicker.DocumentPickerAsset;
}

export type UseFileUploadHookProps = {
  storagePath: StoragePathType;
  fileTypes?: ACCEPTED_MIME_TYPE[];
  beforeUpload?: () => void;
  afterUpload?: (processedFilesArray: ProcessedFile[]) => void;
  onError?: (message?: string) => void;
  maxFileSize?: number;
  isPublic?: boolean;
};

const useFileUpload = ({
  storagePath,
  fileTypes,
  beforeUpload = () => {},
  afterUpload = (processedFilesArray: ProcessedFile[]) => {},
  onError = (message?: string) => {},
  maxFileSize = MAX_FILE_SIZE,
  isPublic = false,
}: UseFileUploadHookProps) => {
  const { currentDomain } = useAppConfig();
  const [generateUploadUrl] = useGenerateUploadUrlMutation();
  const [makeFilePublic] = useMakeFilePublicMutation();

  const handleServerUpload = async ({
    uri,
    mimeType,
    size,
    name,
  }: {
    uri: string;
    mimeType: string;
    size: number;
    name: string;
  }) => {
    try {
      const data = await generateUploadUrl({
        variables: {
          fileName: name,
          storagePath: storagePath,
          fileTypes: fileTypes || ACCEPTED_MIME_TYPES_ARRAY,
        },
      });

      const uploadData = data?.data?.generateUploadUrl?.data[0];
      const { file, signedUrl, uploadedFileMimeType } = uploadData || {};

      if (!signedUrl) throw new Error("Failed to get signed URL");

      // Upload file to GCP using signed URL
      const fileData = await fetch(uri);
      const blob = await fileData.blob();

      await axios.put(signedUrl, blob, {
        headers: {
          "Content-Type": uploadedFileMimeType || mimeType,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
      });

      if (isPublic && currentDomain !== "axiodev.xyz") {
        const result = await makeFilePublic({
          variables: {
            fileId: file.id,
          },
        });

        if (!result.data?.makeFilePublic?.success) {
          throw new Error("Failed to make file public");
        }
      }

      return { file: file as File, signedUrl };
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
      return null;
    }
  };

  const handleFileUpload = async () => {
    await beforeUpload();
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: fileTypes || ACCEPTED_MIME_TYPES_ARRAY,
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets) {
        return;
      }

      const processedFilesArray: {
        file: File;
        signedUrl: string;
        mimeType: string;
        size: number;
        localUploadedFile: DocumentPicker.DocumentPickerAsset;
      }[] = [];

      for (const asset of result.assets) {
        const { uri, mimeType, size, name } = asset;
        // Validate file size
        if (size > maxFileSize) {
          onError?.(
            `File size must be under ${maxFileSize / 1024 / 1024}MB. Yours is ${(size / 1024 / 1024).toFixed(2)}MB.`
          );
          return;
        }

        // Validate mime type
        if (fileTypes && !fileTypes.includes(mimeType as ACCEPTED_MIME_TYPE)) {
          throw new Error(`Invalid file type. Accepted file types: ${fileTypes.join(", ")}.`);
        } else if (!ACCEPTED_MIME_TYPES_ARRAY.includes(mimeType as ACCEPTED_MIME_TYPE)) {
          throw new Error(
            `Invalid file type. Accepted file types: PDF, DOCX, XLSX, PPTX, XLS, DOC, CSV, TXT, PNG, JPEG/JPG, GIF, WEBP.`
          );
        }

        const { file, signedUrl } = await handleServerUpload({ uri, mimeType, size, name });

        processedFilesArray.push({
          file: file,
          signedUrl,
          size,
          mimeType,
          localUploadedFile: asset,
        });
      }

      await afterUpload(processedFilesArray);

      return processedFilesArray;
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
    }
  };

  return { handleFileUpload, handleServerUpload };
};

export default useFileUpload;
